/**
 * The Knowledge browser's two reads (FRONT-1605) — the faceted retrieval
 * engine's HTTP surface, which nothing in the frontend called until now:
 *
 *   - `useKnowledgeSearch` — `GET /api/retrieval/search`, one globally
 *     ranked page of items matching a set of facets, cursor-paginated
 *     (ADR-0023 envelope: `items` / `hasMore` / `nextCursor`, no `total`).
 *   - `useKnowledgeFacetCounts` — `GET /api/retrieval/facets`, the real row
 *     count for that same scope plus every kind and tag present in it with
 *     its own count. This is where the browser's headline number and its
 *     filter chips come from; the listing deliberately does not carry a
 *     total (counting the union twice for a number nobody renders).
 *
 * ── FRONT-1501: every input that changes the request is IN THE KEY ───────
 * TanStack refetches when the KEY changes, not when the options object
 * does, so a facet that rides in `params` but is missing from the key
 * produces a filter control that is fully wired, rendered, clickable — and
 * completely inert, because the first response stays cached forever. The
 * Sketches gallery shipped exactly that. `serializeFacets` below is the one
 * normalizer: it produces the key fragment AND the query string from the
 * same value, so a cache entry can never describe a different query than
 * the one it holds. Its own assertions live in
 * `use-knowledge-search.test.ts` — two different filter states must produce
 * two different keys, the same state the same key, and every keyed variant
 * must nest under the `lists()` invalidation prefix.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

export type KnowledgeSource = 'file' | 'document';
export type Freshness = 'fresh' | 'stale' | 'undated';

/** Wire shape from `KnowledgeHit` (`src/retrieval/types.ts`). Dates cross
 *  the wire as ISO strings. */
export interface KnowledgeHit {
  source: KnowledgeSource;
  /** `user_files.id` for a file, `memory_documents.filename` for a document
   *  — the stable identifier to key UI and tests on, never `title`. */
  ref: string;
  title: string;
  description: string;
  contentType: string | null;
  tags: string[];
  okfType: string | null;
  okfStatus: string | null;
  okfStaleAfter: string | null;
  createdAt: string;
  updatedAt: string;
  matched: boolean;
  rank: number;
  trust: number;
  stale: boolean;
  snippet: string | null;
}

/** Wire shape from `FacetCount` (`src/retrieval/facet-counts.ts`). A null
 *  `value` in `types` is the real "no kind set" bucket. */
export interface FacetCount {
  value: string | null;
  count: number;
}

export interface KnowledgeFacetCounts {
  total: number;
  types: FacetCount[];
  tags: FacetCount[];
}

/** The facets the browser actually drives. `status` and the date range the
 *  engine also supports are deliberately absent — nothing on screen sets
 *  them, and a key fragment for an input no control can change is a key
 *  fragment that can silently go stale. */
export interface KnowledgeFacetState {
  /** OKF kinds, ANY-of. */
  type?: string[];
  /** Tags, ANY-of, matched case-insensitively server-side. */
  tags?: string[];
  freshness?: Freshness;
}

/**
 * One normalized, order-independent description of a facet state — used
 * BOTH as the query-key fragment and to build the request's query string,
 * so the two can never disagree (FRONT-1501). Sorted and lowercased so
 * picking the same two tags in the other order is genuinely the same cache
 * entry rather than a second copy of it.
 */
export function serializeFacets(facets: KnowledgeFacetState): string {
  const params = new URLSearchParams();
  const type = [...(facets.type ?? [])].map((t) => t.trim().toLowerCase()).filter(Boolean).sort();
  const tags = [...(facets.tags ?? [])].map((t) => t.trim().toLowerCase()).filter(Boolean).sort();
  if (type.length > 0) params.set('type', type.join(','));
  if (tags.length > 0) params.set('tags', tags.join(','));
  if (facets.freshness) params.set('freshness', facets.freshness);
  return params.toString();
}

export const knowledgeSearchKeys = {
  all: ['knowledge-search'] as const,
  /** Invalidation prefix for every listing, whatever its facets. */
  lists: () => [...knowledgeSearchKeys.all, 'list'] as const,
  list: (facets: KnowledgeFacetState) => [...knowledgeSearchKeys.lists(), serializeFacets(facets)] as const,
  /** Facet counts nest under their own prefix — a listing page arriving
   *  must not look like a counts entry, and vice versa. */
  counts: () => [...knowledgeSearchKeys.all, 'counts'] as const,
  count: (facets: KnowledgeFacetState) => [...knowledgeSearchKeys.counts(), serializeFacets(facets)] as const,
} as const;

/** ADR-0023 envelope. */
interface KnowledgePageResponse {
  items: KnowledgeHit[];
  hasMore: boolean;
  nextCursor: string | null;
}

/** Page size. The engine hard-caps at 50 (`clampLimit`); asking for that
 *  keeps a browse to one round trip in the common case. */
const PAGE_SIZE = 50;

function searchUrl(facets: KnowledgeFacetState, cursor: string | null): string {
  const qs = serializeFacets(facets);
  const parts = [qs, `limit=${PAGE_SIZE}`];
  if (cursor) parts.push(`cursor=${encodeURIComponent(cursor)}`);
  return `/api/retrieval/search?${parts.filter(Boolean).join('&')}`;
}

/**
 * One facet-scoped listing, paginated. `enabled` is what keeps this lazy:
 * the browser modal mounts closed, and a closed modal must not fetch.
 *
 * The engine returns nothing at all for a request with no facet AND no
 * query (`isEmptyRequest`, the guard that stops a bare call dumping the
 * whole corpus), so a caller that passes an empty facet state gets an empty
 * list — never "everything".
 */
export function useKnowledgeSearch(facets: KnowledgeFacetState, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: knowledgeSearchKeys.list(facets),
    queryFn: ({ pageParam }) => api.get<KnowledgePageResponse>(searchUrl(facets, pageParam)),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : null),
    enabled,
  });
}

/** The real count for a scope, plus what each other filter would give.
 *  Same laziness contract as `useKnowledgeSearch`. */
export function useKnowledgeFacetCounts(facets: KnowledgeFacetState, enabled: boolean) {
  return useQuery({
    queryKey: knowledgeSearchKeys.count(facets),
    queryFn: () => api.get<KnowledgeFacetCounts>(`/api/retrieval/facets?${serializeFacets(facets)}`),
    enabled,
  });
}
